'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import HelpBox from '@booking/components/HelpBox'
import SummaryBox from '@booking/components/SummaryBox'
import ContinueButton from '@booking/components/ContinueButton'
import cn from 'classnames'

import CustomerDetailsForm from './components/CustomerDetailsForm'

import { usePersistCustomerPayload } from './hooks/usePersistCustomerPayload'
import { buildCustomerPayload, getCustomerErrorMessages } from './lib/customerDetails'
import type { CustomerDetailsProps, CustomerFormPayload, CustomerFormFields } from './types'
import { handleAppError } from '@booking/lib/errors/handleAppError'
import { ApiError, ValidationError } from '@booking/lib/errors/apiError'
import { useLocale, useTranslations } from 'next-intl'
import shared from '@booking/index.module.scss'

export default function CustomerDetails({
  hideSessionTermsCheckbox = false,
}: CustomerDetailsProps) {
  const state = useBookingState()
  const context = state.config.context
  const { dispatch, onCompleteCustomerData } = useBookingActionsCtx()
  const locale = useLocale()
  const t = useTranslations('booking')

  //const cartId = state.cart?.id

  const [errors, setErrors] = useState<ValidationError[]>([])
  const [showVatField, setShowVatField] = useState(false)
  const [values, setValues] = useState<CustomerFormFields>(state.customerData)

  const headingRef = useRef<HTMLHeadingElement>(null)

  const hasError = useCallback(
    (fieldName: string) => {
      return errors.some((error) => error.key === fieldName)
    },
    [errors],
  )

  const handleValueChange = useCallback(
    <K extends keyof CustomerFormFields>(field: K, value: CustomerFormFields[K]) => {
      setValues((previous) => ({
        ...previous,
        [field]: value,
      }))
    },
    [],
  )

  const resolvedValues = useMemo<CustomerFormFields>(() => {
    if (!hideSessionTermsCheckbox) return values

    return {
      ...values,
      acceptSessionTerms: true,
    }
  }, [hideSessionTermsCheckbox, values])

  const payload = useMemo<CustomerFormPayload>(() => {
    return buildCustomerPayload(resolvedValues, locale, context)
  }, [locale, resolvedValues, context])

  usePersistCustomerPayload(payload)

  // Optional: dev helper (button) to prefill values.
  const inputTestData = useCallback(() => {
    setValues((previous) => ({
      ...previous,
      firstName: 'Andy',
      lastName: 'Smith',
      email: 'andy@weaccelerategrowth.com',
      emailConfirm: 'andy@weaccelerategrowth.com',
      phone: '07826015687',
      country: 'GB',
      acceptSessionTerms: true,
      acceptCommunication: true,
      acceptTerms: true,
    }))
  }, [])

  const handleComplete = useCallback(async () => {
    setErrors([])

    /*if (!cartId) {
      // cart should exist at this point; keep UX stable
      setErrors([{ field: 'cart', conditions: [{ message: MISSING_CART_ERROR }] }])
      headingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }*/

    try {
      await onCompleteCustomerData(payload)
    } catch (e) {
      const appError = handleAppError(e, { dispatch, t })
      if (appError === 'validation_error' && e instanceof ApiError) {
        if (e.data && e.data.errors) setErrors(e.data.errors)
      }
      headingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      throw e // rethrow so continue button error handler picks it up
    }
  }, [onCompleteCustomerData, payload, dispatch, t])

  const errorMessages = useMemo(() => {
    return getCustomerErrorMessages(errors)
  }, [errors])

  return (
    <div className={cn(shared.box)}>
      <div className={cn(shared.inner)}>
        <div className={cn(shared.leftColumn)}>
          <CustomerDetailsForm
            headingRef={headingRef}
            hideSessionTermsCheckbox={hideSessionTermsCheckbox}
            errorMessages={errorMessages}
            hasError={hasError}
            onComplete={handleComplete}
            onFillData={inputTestData}
            onShowVatField={() => setShowVatField(true)}
            onValueChange={handleValueChange}
            showVatField={showVatField}
            values={resolvedValues}
          />

          <ContinueButton className={cn(shared.continue)} onContinue={handleComplete} />
        </div>

        <div className={cn(shared.rightColumn)}>
          <SummaryBox onContinue={handleComplete} />
          <HelpBox />
        </div>
      </div>
    </div>
  )
}
